import React, { useState, useEffect, Fragment } from "react";
import {
  RotateCcwKey,
  UserRoundX,
  LogOut,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Menu, Transition } from "@headlessui/react";
import { useAppSelector, useAppDispatch } from "store/hooks/hooks";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import OtpInput from "components/OtpInputComponent/OtpInput";
import {
  deleteUserAccount,
  fetchUserById,
  selectUser,
  selectUserLoaded,
  selectUserLoading,
} from "store/slices/userSlice";
import LogoManagement from "./LogoManagement";
import ToolsDashboard from "./ToolsDashboard";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import LucideIcon from "utils/LucideIcon";
import Tippy from "@tippyjs/react";
import ConfirmDialog from "shared/Dialog/ConfirmDialog ";
import userImg from "../../assets/images/userPlaceholderImg.jpg";
import toast from "react-hot-toast";
import "tippy.js/dist/tippy.css";
import {
  logoutUser,
  resendVerificationCode,
  updateUser,
  verifyOtp,
} from "store/slices/authSlice";
import { fetchUserLogos } from "store/slices/logosSlice";
import { fetchUserMockups } from "store/slices/mockupSlice";
import { fetchUserCatalogs } from "store/slices/catalogSlice";
import {
  selectIsUserTerminated,
  setIsUserTerminated,
} from "store/slices/uiSlice";
import { ClipLoader } from "react-spinners";
import { IUserData } from "Interfaces/interface.user";
import Truncate from "utils/Truncate";
import { COMPANY_NAME_PATTERN } from "utils/validationRegex";
import { Alert } from "@/shared/Alert/Alert";
import { AlertDescription } from "@/components/UI/alert";

type VerificationType = "email" | "phone";
type VerificationState = {
  type: VerificationType | null;
  value: string;
  inProgress: boolean;
};

const MyProfile = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const isUserLoaded = useAppSelector(selectUserLoaded);
  const isUserLoading = useAppSelector(selectUserLoading);
  const [userData, setUserData] = useState<IUserData>({
    fullName: user?.name || "",
    companyName: user?.company || "",
    email: user?.emailAddress || "",
    phoneNumber: user?.phoneNumber || "",
    emailVerified: user?.emailVerified || false,
    phoneNumberVerified: user?.phoneNumberVerified || false,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editedFullName, setEditedFullName] = useState(userData.fullName);
  const [editedCompanyName, setEditedCompanyName] = useState(
    userData.companyName
  );
  const [editedPhoneNumber, setEditedPhoneNumber] = useState(
    userData.phoneNumber
  );
  const [verificationState, setVerificationState] = useState<VerificationState>(
    {
      type: null,
      value: "",
      inProgress: false,
    }
  );
  const [otp, setOtp] = useState("");
  const [verificationError, setVerificationError] = useState("");
  const [fullNameError, setFullNameError] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState<string | null>("");
  const [companyNameError, setCompanyNameError] = useState<string | null>("");
  const [isSaving, setIsSaving] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showVerifyidTooltip, setShowVerifyidTooltip] =
    useState<boolean>(false);

  const isLoggingOut = useAppSelector(selectIsUserTerminated);
  const [mobileVerify, setMobileVerify] = useState(false);
  const [isResendingOtp, setIsResendingOtp] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    dispatch(fetchUserById());
  }, [dispatch]);

  useEffect(() => {
    if (isLoggingOut) return;

    dispatch(fetchUserLogos());
    dispatch(fetchUserMockups());
    dispatch(fetchUserCatalogs());
  }, [dispatch, isLoggingOut]);

  useEffect(() => {
    if (user) {
      const newUserData = {
        fullName: user.name?.trim() || "",
        companyName: user.company || "",
        email: user.emailAddress || "",
        phoneNumber: user.phoneNumber || "",
        emailVerified: user.emailVerified || false,
        phoneNumberVerified: user.phoneNumberVerified || false,
      };
      setUserData(newUserData);
      setEditedFullName(newUserData.fullName);
      setEditedCompanyName(newUserData.companyName);
      setEditedPhoneNumber(newUserData.phoneNumber);
    }
  }, [user]);

  const handleVerify = async (
    type: "email" | "phone",
    overrideValue?: string
  ) => {
    const value = overrideValue?.trim();
    const verifyValue = value || `${editedPhoneNumber.replace(/\D/g, "")}`;
    setMobileVerify(true);

    if (!verifyValue) {
      toast.error(`Please enter a valid ${type} to verify.`);
      setMobileVerify(false);
      return;
    }
    setOtp("");
    setVerificationError("");
    try {
      await dispatch(
        resendVerificationCode({
          type,
          value: verifyValue,
        })
      ).unwrap();
      setVerificationState({
        type,
        value: verifyValue,
        inProgress: true,
      });
    } catch (err: any) {
      setVerificationState((prev) => ({ ...prev, inProgress: false }));
    } finally {
      setMobileVerify(false);
    }
  };

  const handleOtpVerify = async () => {
    if (!otp || otp.length !== 6) {
      setVerificationError("Please enter a valid 6-digit OTP.");
      return;
    }
    setIsLoading(true);
    try {
      await dispatch(
        verifyOtp({
          type: verificationState.type!,
          value: verificationState.value,
          code: otp,
        })
      ).unwrap();

      if (verificationState.type === "email") {
        setUserData((prev) => ({
          ...prev,
          emailVerified: true,
        }));
      } else if (verificationState.type === "phone") {
        setUserData((prev) => ({
          ...prev,
          phoneNumberVerified: true,
        }));
      }

      setVerificationState({ type: null, value: "", inProgress: false });
    } catch (err: any) {
      setVerificationError(err.message || "Invalid or expired OTP");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!verificationState.type) return;
    setIsResendingOtp(true);

    try {
      await dispatch(
        resendVerificationCode({
          type: verificationState.type,
          value: verificationState.value,
        })
      ).unwrap();
      setIsResendingOtp(false);
    } catch (err: any) {
      setIsResendingOtp(false);
      return err;
    }
  };

  const validateFullName = (value: string) => {
    const fullNameRegex = /^(?=(?:.*[A-Za-z]){2,})[A-Za-z\s]+$/;
    if (!value.trim()) {
      return "Full name required.";
    } else if (value.length < 2) {
      return "Min 2 characters.";
    } else if (value.length > 255) {
      return "Max 255 characters.";
    } else if (!fullNameRegex.test(value)) {
      return "Only letters allowed";
    }
    return "";
  };

  const validatePhoneNumber = (value: string) => {
    const digitsOnlyRegex = /^\d+$/;
    if (!value.trim()) {
      return "";
    }
    if (!digitsOnlyRegex.test(value)) {
      return "Only digits allowed.";
    } else if (value.length !== 10) {
      return "Must be 10 digits.";
    } else if (value[0] === "0") {
      return "Must not start with 0.";
    }
    return "";
  };

  const validateCompanyName = (value: string) => {
    if (!value.trim()) {
      return "";
    }
    if (value.length < 2) {
      return "Min 2 characters.";
    } else if (value.length > 255) {
      return "Max 255 characters.";
    } else if (!COMPANY_NAME_PATTERN.test(value)) {
      return "Please enter a valid company name";
    }
    return "";
  };

  const handleBlurFullName = () => {
    const error = validateFullName(editedFullName);
    setFullNameError(error);
  };

  const handleBlurPhoneNumber = () => {
    const error = validatePhoneNumber(editedPhoneNumber);
    setPhoneNumberError(error);
  };

  const handleBlurCompanyName = () => {
    const error = validateCompanyName(editedCompanyName);
    setCompanyNameError(error);
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setFullNameError("");
    setPhoneNumberError("");
    setCompanyNameError("");
    setVerificationState({ type: null, value: "", inProgress: false });
  };

  const handleSaveClick = async () => {
    let newFullNameError = validateFullName(editedFullName);
    let newPhoneNumberError = validatePhoneNumber(editedPhoneNumber);
    let newCompanyNameError = validateCompanyName(editedCompanyName);

    setFullNameError(newFullNameError);
    setPhoneNumberError(newPhoneNumberError);
    setCompanyNameError(newCompanyNameError);

    if (newFullNameError || newPhoneNumberError || newCompanyNameError) {
      toast.error("Please correct the errors.");
      return;
    }

    setIsSaving(true);
    const requiresPhoneVerify = editedPhoneNumber !== userData.phoneNumber;

    try {
      await dispatch(
        updateUser({
          name: editedFullName.trim(),
          phoneNumber: editedPhoneNumber.replace(/\D/g, ""),
          emailAddress: userData.email,
          company: editedCompanyName.trim(),
        })
      ).unwrap();

      setUserData({
        ...userData,
        fullName: editedFullName.trim(),
        companyName: editedCompanyName.trim(),
        phoneNumber: editedPhoneNumber.replace(/\D/g, ""),
        phoneNumberVerified:
          userData.phoneNumberVerified || !requiresPhoneVerify,
      });

      setIsEditing(false);
      if (
        !userData.phoneNumberVerified &&
        requiresPhoneVerify &&
        editedPhoneNumber !== ""
      ) {
        handleVerify("phone");
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to update information.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelClick = () => {
    setEditedFullName(userData.fullName);
    setEditedCompanyName(userData.companyName);
    setEditedPhoneNumber(userData.phoneNumber);
    setFullNameError("");
    setPhoneNumberError("");
    setCompanyNameError("");
    setIsEditing(false);
    setVerificationState({ type: null, value: "", inProgress: false });
  };

  const handleUserAccountDelete = async () => {
    setDeletingAccount(true);
    try {
      dispatch(setIsUserTerminated(true));
      await dispatch(deleteUserAccount()).unwrap();
      setDeletingAccount(false);
      setShowDialog(false);
      navigate("/");
    } catch (err: any) {
      dispatch(setIsUserTerminated(false));
      setDeletingAccount(false);
      setShowDialog(false);
    }
  };

  const handleLogout = async () => {
    try {
      dispatch(setIsUserTerminated(true));
      await dispatch(logoutUser()).unwrap();
      navigate("/");
    } catch (err: any) {
      dispatch(setIsUserTerminated(false));
    }
  };

  const isProfileIncomplete =
    !userData.phoneNumber || !userData.fullName || !userData.companyName;

  const changePassword = () => {
    navigate(`/changePassword?email=${userData.email}`);
  };

  useEffect(() => {
    if (!userData.phoneNumberVerified) {
      setShowTooltip(true);
      const timer = setTimeout(() => setShowTooltip(false), 2500);
      return () => clearTimeout(timer);
    }
  }, [isEditing, userData.phoneNumberVerified, userData.phoneNumber]);

  const seeTooltip = () => {
    if (userData.phoneNumberVerified) {
      setShowVerifyidTooltip(true);
      const timer = setTimeout(() => setShowVerifyidTooltip(false), 2500);
      return () => clearTimeout(timer);
    } else {
      setShowTooltip(true);
      const timer = setTimeout(() => setShowTooltip(false), 2500);
      return () => clearTimeout(timer);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="container py-8 lg:pb-32 space-y-8">
        <div>
          <div className="rounded-2xl relative overflow-hidden">
            {!isEditing && (
              <Tippy
                content={
                  <span className="text-xs font-inter">
                    Your profile is missing some information. Click to complete
                    it.
                  </span>
                }
                placement="top"
                interactive={true}
                theme="gradient"
                appendTo={() => document.body}
                disabled={!isProfileIncomplete}
              >
                <button
                  onClick={handleEditClick}
                  className="absolute top-20 md:top-6 right-6 md:right-20 p-3 bg-white/95 backdrop-blur-sm rounded-2xl transition-all duration-300 shadow-lg z-10"
                >
                  {isProfileIncomplete && (
                    <span className="absolute top-0 right-0 h-3.5 w-3.5 bg-red-500 border-2 border-white rounded-full z-20" />
                  )}
                  <LucideIcon name="Pencil" size={18} />
                </button>
              </Tippy>
            )}

            {!isEditing && (
              <div className="absolute top-6 right-6 lg:right-6 z-10">
                <Menu as="div" className="relative">
                  <Menu.Button className="p-3 rounded-2xl bg-white/95 backdrop-blur-sm transition-all duration-300 shadow-lg">
                    <LucideIcon name="EllipsisVertical" size={18} />
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-150"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 mt-3 p-3 w-52 origin-top-right rounded-2xl bg-white/90 backdrop-blur-md shadow-2xl border border-white/20 focus:outline-none">
                      <div className="mb-2">
                        <Menu.Item>
                          <button
                            onClick={changePassword}
                            className="flex items-center justify-between text-sm p-3 hover:bg-blue-50 rounded-xl duration-300 ease-out hover:text-blue-600 group w-full text-left font-inter"
                          >
                            <span>Change Password</span>
                            <RotateCcwKey
                              size={16}
                              className="group-hover:rotate-12 transition-transform duration-300"
                            />
                          </button>
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => setShowDialog(true)}
                              className={`flex items-center justify-between text-sm p-3 hover:bg-red-50 rounded-xl duration-300 ease-out hover:text-red-600 group w-full text-left font-inter ${active
                                ? "bg-red-50 text-red-600"
                                : "text-gray-700"
                                }`}
                            >
                              <span>Delete Account</span>
                              <UserRoundX
                                size={16}
                                className="group-hover:shake transition-transform duration-300"
                              />
                            </button>
                          )}
                        </Menu.Item>
                      </div>
                      <div className="pt-2 border-t border-gray-200/50">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={handleLogout}
                              className={`flex items-center justify-between text-sm p-3 w-full text-left hover:bg-orange-50 rounded-xl duration-300 ease-out hover:text-orange-600 group font-inter${active
                                ? "bg-orange-50 text-orange-600"
                                : "text-gray-700"
                                }`}
                            >
                              <span>Sign Out</span>
                              <LogOut
                                size={16}
                                className="group-hover:translate-x-1 transition-transform duration-300"
                              />
                            </button>
                          )}
                        </Menu.Item>
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            )}

            <div className="relative bg-primary-gradient rounded-2xl overflow-hidden">
              <div className="relative p-6 sm:p-8 lg:p-10">
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 lg:gap-8">
                  <div className="relative group flex-shrink-0">
                    <div className="relative">
                      <img
                        src={userImg}
                        alt="Profile"
                        className="w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 rounded-2xl border-4 border-white shadow-xl object-cover ring-4 ring-blue-100/50 duration-300"
                      />
                    </div>
                  </div>
                  <div className="flex-grow text-center sm:text-left">
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2 md:mb-4 font-inter truncate">
                      <Truncate text={userData.fullName} maxLength={15} />
                    </h1>
                    <label className="text-xs md:text-sm xl:text-base font-semibold text-gray-100/80 tracking-wider font-inter">
                      <Truncate text={userData.email} maxLength={30} />
                    </label>
                  </div>
                </div>
              </div>
              <div className="px-3 sm:px-8 lg:px-10 pb-6 sm:pb-8 lg:pb-10">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
                  <div className="group">
                    <div className="h-full p-4 sm:p-5 bg-white/70 backdrop-blur-sm rounded-2xl border border-white/40 shadow-lg hover:shadow-xl transition-all duration-300 hover:bg-white/80 hover:border-white/60">
                      <div className="flex items-start gap-3">
                        <div className="p-2.5 bg-blue-100 rounded-xl group-hover:bg-blue-200 transition-colors duration-300 flex-shrink-0">
                          <LucideIcon
                            name="User"
                            size={18}
                            className="text-blue-600"
                          />
                        </div>
                        <div className="flex-grow min-w-0">
                          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1 block font-inter">
                            Full Name
                          </label>
                          {isUserLoading ? (
                            <p className="flex items-center">
                              <ClipLoader
                                loading={true}
                                size={16}
                                color="blue"
                                className="ml-4"
                              />
                            </p>
                          ) : (
                            isUserLoaded && (
                              <>
                                {isEditing ? (
                                  <div>
                                    <input
                                      type="text"
                                      value={editedFullName}
                                      placeholder="Your name"
                                      disabled={isLoading || isSaving}
                                      onChange={(e) => {
                                        setEditedFullName(e.target.value);
                                        setFullNameError("");
                                      }}
                                      onBlur={handleBlurFullName}
                                      className={`w-full p-2.5 bg-white border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 text-sm disabled:cursor-not-allowed disabled:opacity-50 ${fullNameError
                                        ? "border-red-300 ring-2 ring-red-100 focus:ring-red-200"
                                        : "border-gray-200 focus:border-blue-300"
                                        }`}
                                    />
                                    {fullNameError && (
                                      <p className="text-red-500 text-xs mt-2 flex items-center">
                                        <span className="w-1 h-1 bg-red-500 rounded-full mr-2 flex-shrink-0"></span>
                                        <span className="truncate">
                                          {fullNameError}
                                        </span>
                                      </p>
                                    )}
                                  </div>
                                ) : userData.fullName && isUserLoaded ? (
                                  <p className="text-gray-800 font-semibold text-sm truncate font-inter">
                                    {userData.fullName}
                                  </p>
                                ) : (
                                  <p className="text-red-500 text-sm font-medium">
                                    Missing name
                                  </p>
                                )}
                              </>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="group">
                    <div className="h-full p-4 sm:p-5 bg-white/70 backdrop-blur-sm rounded-2xl border border-white/40 shadow-lg hover:shadow-xl transition-all duration-300 hover:bg-white/80 hover:border-white/60">
                      <div className="flex items-start gap-3">
                        <div className="p-2.5 bg-purple-100 rounded-xl group-hover:bg-purple-200 transition-colors duration-300 flex-shrink-0">
                          <LucideIcon
                            name="Phone"
                            size={18}
                            className="text-purple-600"
                          />
                        </div>
                        <div className="flex-grow min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider font-inter">
                              Contact Number
                            </label>
                            <div>
                              {isEditing ? (
                                <Tippy
                                  content={
                                    <span className="text-xs font-inter">
                                      Please enter an{" "}
                                      <strong>active WhatsApp number</strong>.
                                    </span>
                                  }
                                  visible={showTooltip}
                                  onShow={() => setShowTooltip(true)}
                                  onHide={() => setShowTooltip(false)}
                                  placement="top"
                                  animation="shift-away"
                                  theme="info"
                                  className="tippy-box"
                                >
                                  <div
                                    onMouseEnter={() => setShowTooltip(true)}
                                    onMouseLeave={() => setShowTooltip(false)}
                                  >
                                    <LucideIcon
                                      name="CircleAlert"
                                      className="!text-red-500 hover:text-red-500 w-4 h-4"
                                    />
                                  </div>
                                </Tippy>
                              ) : userData.phoneNumber &&
                                userData.phoneNumberVerified ? (
                                <Tippy
                                  content={
                                    <span className="text-xs font-inter">
                                      Contact number verified
                                    </span>
                                  }
                                  visible={showVerifyidTooltip}
                                  placement="top"
                                  animation="shift-away"
                                  theme="verified"
                                  className="tippy-box"
                                >
                                  <div onMouseEnter={seeTooltip}>
                                    <LucideIcon
                                      name="CheckCheck"
                                      className="text-green-600 hover:text-green-600 w-4 h-4"
                                    />
                                  </div>
                                </Tippy>
                              ) : userData.phoneNumber &&
                                !userData.phoneNumberVerified ? (
                                <Tippy
                                  content={
                                    <span className="text-xs font-inter">
                                      Verify your contact number
                                    </span>
                                  }
                                  visible={showTooltip}
                                  placement="top"
                                  animation="shift-away"
                                  theme="gradient"
                                  className="tippy-box"
                                >
                                  <div onMouseEnter={seeTooltip}>
                                    <LucideIcon
                                      name="CircleAlert"
                                      className="!text-red-500 hover:text-red-500 w-4 h-4"
                                    />
                                  </div>
                                </Tippy>
                              ) : null}
                            </div>
                          </div>
                          {isUserLoading ? (
                            <p className="flex items-center">
                              <ClipLoader
                                loading={true}
                                size={16}
                                color="blue"
                                className="ml-4"
                              />
                            </p>
                          ) : (
                            isUserLoaded && (
                              <>
                                {isEditing ? (
                                  <div>
                                    <input
                                      type="tel"
                                      value={editedPhoneNumber}
                                      disabled={isLoading || isSaving}
                                      placeholder="Mobile number"
                                      onChange={(e) => {
                                        setEditedPhoneNumber(e.target.value);
                                        setPhoneNumberError("");
                                      }}
                                      onBlur={handleBlurPhoneNumber}
                                      className={`w-full p-2.5 bg-white border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 text-sm disabled:cursor-not-allowed disabled:opacity-50 ${phoneNumberError
                                        ? "border-red-300 ring-2 ring-red-100 focus:ring-red-200"
                                        : "border-gray-200 focus:border-blue-300"
                                        }`}
                                    />
                                    {phoneNumberError && (
                                      <p className="text-red-500 text-xs mt-2 flex items-center">
                                        <span className="w-1 h-1 bg-red-500 rounded-full mr-2 flex-shrink-0"></span>
                                        <span className="truncate">
                                          {phoneNumberError}
                                        </span>
                                      </p>
                                    )}
                                  </div>
                                ) : userData.phoneNumber && isUserLoaded ? (
                                  <div className="flex items-center gap-2 md:gap-6">
                                    <p className="text-gray-800 font-semibold text-sm truncate font-inter">
                                      {userData.phoneNumber}
                                    </p>
                                    {!userData.phoneNumberVerified &&
                                      userData.phoneNumber && (
                                        <button
                                          onClick={() => handleVerify("phone")}
                                          className="text-xs text-blue-400 hover:text-blue-700 font-medium transition-colors duration-200 disabled:text-gray-400 disabled:cursor-not-allowed font-inter"
                                          disabled={mobileVerify}
                                        >
                                          {mobileVerify ? (
                                            <ClipLoader
                                              loading={true}
                                              size={16}
                                              color="blue"
                                            />
                                          ) : (
                                            "Verify"
                                          )}
                                        </button>
                                      )}
                                  </div>
                                ) : (
                                  <p className="text-red-500 text-sm font-medium">
                                    Missing mobile number
                                  </p>
                                )}
                              </>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="group">
                    <div className="h-full p-4 sm:p-5 bg-white/70 backdrop-blur-sm rounded-2xl border border-white/40 shadow-lg hover:shadow-xl transition-all duration-300 hover:bg-white/80 hover:border-white/60">
                      <div className="flex items-start gap-3">
                        <div className="p-2.5 bg-blue-100 rounded-xl group-hover:bg-blue-200 transition-colors duration-300 flex-shrink-0">
                          <LucideIcon
                            name="Building2"
                            size={18}
                            className="text-blue-600"
                          />
                        </div>
                        <div className="flex-grow min-w-0">
                          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1 block font-inter">
                            Company Name
                          </label>
                          {isUserLoading ? (
                            <p className="flex items-center">
                              <ClipLoader
                                loading={true}
                                size={16}
                                color="blue"
                                className="ml-4"
                              />
                            </p>
                          ) : (
                            isUserLoaded && (
                              <>
                                {isEditing ? (
                                  <div className="w-full">
                                    <input
                                      type="text"
                                      value={editedCompanyName}
                                      disabled={isLoading || isSaving}
                                      onChange={(e) => {
                                        setEditedCompanyName(e.target.value);
                                        setCompanyNameError("");
                                      }}
                                      onBlur={handleBlurCompanyName}
                                      placeholder="Company name"
                                      className={`w-full p-2.5 bg-white border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 text-sm disabled:cursor-not-allowed disabled:opacity-50 ${companyNameError
                                        ? "border-red-300 ring-2 ring-red-100 focus:ring-red-200"
                                        : "border-gray-200 focus:border-blue-300"
                                        }`}
                                    />

                                    {companyNameError && (
                                      <p className="text-red-500 text-xs mt-2 flex items-center">
                                        <span className="w-1 h-1 bg-red-500 rounded-full mr-2 flex-shrink-0"></span>
                                        <span className="truncate">
                                          {companyNameError}
                                        </span>
                                      </p>
                                    )}
                                  </div>
                                ) : userData.companyName && isUserLoaded ? (
                                  <p className="text-gray-800 font-semibold text-sm truncate font-inter">
                                    {userData.companyName}
                                  </p>
                                ) : (
                                  <p className="text-red-500 text-sm font-medium">
                                    Missing company name
                                  </p>
                                )}
                              </>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {isEditing && (
                  <div className="flex flex-col-reverse justify-end sm:flex-row gap-3 sm:gap-4 mt-6 sm:mt-8">
                    <ButtonSecondary
                      variant="cancel"
                      onClick={handleCancelClick}
                      disabled={isSaving || isLoading}
                      size="lg"
                    >
                      <span>Cancel</span>
                    </ButtonSecondary>
                    <ButtonSecondary
                      onClick={handleSaveClick}
                      loading={isSaving}
                      disabled={
                        isLoading ||
                        isSaving ||
                        !editedFullName.trim() ||
                        (editedFullName.trim() === userData.fullName &&
                          editedCompanyName.trim() === userData.companyName &&
                          editedPhoneNumber.trim() === userData.phoneNumber)
                      }
                    >
                      {!isSaving && (
                        <div className="flex items-center justify-center gap-2">
                          {!userData.phoneNumberVerified &&
                            editedPhoneNumber !== "" &&
                            editedPhoneNumber.trim() !== userData.phoneNumber
                            ? "Verify & Save"
                            : "Save Changes"}
                        </div>
                      )}
                    </ButtonSecondary>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <Alert dismissible={true} className="mt-4 border-blue-200 bg-blue-50">
          <AlertCircle className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-600">
            <p>
              Your number is taken for sharing thoughtful updates to help you
              find the right gift not to clutter your inbox with
              <strong> SPAM </strong>messages
            </p>
          </AlertDescription>
        </Alert>
        <LogoManagement />
        <ToolsDashboard />
        <AnimatePresence>
          {verificationState.inProgress && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm !mt-0">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white p-8 rounded-3xl shadow-2xl w-full max-w-md border border-white/20"
              >
                <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center font-inter">
                  Verify your {verificationState.type}
                </h2>
                <div className="mb-6">
                  <OtpInput
                    value={otp}
                    onChange={setOtp}
                    length={6}
                    disabled={isLoading}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <ButtonSecondary
                    variant="cancel"
                    disabled={isLoading}
                    onClick={() => {
                      setVerificationState({
                        type: null,
                        value: "",
                        inProgress: false,
                      });
                      setOtp("");
                    }}
                  >
                    Cancel
                  </ButtonSecondary>
                  <ButtonPrimary
                    onClick={handleOtpVerify}
                    loading={isLoading}
                    disabled={isLoading}
                    className="flex-1 px-6 py-3 font-medium transition-all duration-300 hover:scale-105 disabled:opacity-50"
                  >
                    Verify
                  </ButtonPrimary>
                </div>
                <p className="text-center mt-4 mb-1 text-sm text-gray-600 font-inter">
                  Didn't receive the code?{" "}
                  <button
                    onClick={handleResendOtp}
                    disabled={isLoading}
                    className="text-blue-600 hover:underline disabled:text-gray-500 font-inter disabled:pointer-events-none disabled:opacity-50"
                  >
                    {isResendingOtp ? (
                      <ClipLoader
                        loading={true}
                        size={14}
                        color="blue"
                        className="mx-4"
                      />
                    ) : (
                      "Resend OTP"
                    )}
                  </button>
                </p>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
        <ConfirmDialog
          isOpen={showDialog}
          loading={deletingAccount}
          onClose={() => setShowDialog(false)}
          onConfirm={handleUserAccountDelete}
          title="Delete Account"
          message="Are you sure? Your account will be permanently deleted if not reactivated within 45 days."
        />
      </div>
    </div>
  );
};

export default MyProfile;
